import React, { useEffect, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { setOrgSessionPolicy } from '@/lib/sessionKeepAlive';
import { setOrgFormatPrefs } from '@/lib/orgFormat';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { setCurrentTenant } from '@/features/auth/state/tenant-state/tenantSlice';
import {
  getCurrentTenant, updateTenant, uploadTenantLogo, saveTenantLogo, syncPayrollBrandingLogo,
  type TenantDetails, type TenantSettings,
} from '@/lib/hrApi';

/** Payslip PDFs embed raster images only — convert SVG/WebP to PNG client-side
 *  (transparency preserved) so one logo works in the app and on the payslip. */
async function toRasterLogo(file: File): Promise<File> {
  if (/(png|jpe?g|gif)$/i.test(file.type)) return file;
  const src = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = src;
    });
    const maxW = 600;
    const natW = img.naturalWidth || maxW;
    const natH = img.naturalHeight || Math.round(maxW / 3);
    const scale = natW > maxW ? maxW / natW : 1;
    const w = Math.max(1, Math.round(natW * scale));
    const h = Math.max(1, Math.round(natH * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
    const blob: Blob = await new Promise((res) => canvas.toBlob((b) => res(b!), 'image/png'));
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.png', { type: 'image/png' });
  } finally {
    URL.revokeObjectURL(src);
  }
}
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import { Button } from '@/features/shared/components/ui/button';
import { Switch } from '@/features/shared/components/ui/switch';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Settings, Users, Bell, Shield, Palette, Building2, Gift, Loader2, Mail } from 'lucide-react';
import { getReferralProgram, setReferralProgramEnabled } from '@/lib/referralApi';
import { EmailIntegrationsPage } from './EmailIntegrationsPage';
import { CalendarSyncSettingsCard } from './CalendarSyncSettingsCard';
import { BenefitsSettingsCard } from './BenefitsSettingsCard';
import { FinancialProductsSettingsCard } from './FinancialProductsSettingsCard';
import { CommerceSettingsCard } from './CommerceSettingsCard';
import { NotificationPreferencesCard } from './NotificationPreferencesCard';
import { ReadReceiptsCard } from './ReadReceiptsCard';
import { NotificationPolicyCard } from './NotificationPolicyCard';
import { EmailDeliveryTrailCard } from './EmailDeliveryTrailCard';

const TIMEZONES = ['UTC', 'Africa/Dar_es_Salaam', 'Africa/Nairobi', 'Africa/Kigali', 'Africa/Kampala', 'Africa/Lagos', 'Africa/Johannesburg', 'Europe/London', 'America/New_York', 'America/Los_Angeles', 'Asia/Dubai'];
const CURRENCIES = ['TZS', 'KES', 'RWF', 'UGX', 'USD', 'EUR', 'GBP', 'AED'];
const DATE_FORMATS = ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];

// Defaults for the settings JSON sub-objects.
const NOTIF_DEFAULTS = { emailNotifications: true, leaveRequests: true, weeklyDigest: false };
const SECURITY_DEFAULTS = { twoFactorRequired: false, sessionTimeout: true, sessionTimeoutMinutes: 30 };
const SESSION_TIMEOUT_CHOICES = [15, 30, 60, 120, 240];
const LANGUAGES = [ { value: 'en', label: 'English' }, { value: 'sw', label: 'Kiswahili' } ];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const ACCESS_DEFAULTS = { autoApprove: false, requireManagerApproval: true, selfServiceProfile: true };

export const SettingsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const dispatch = useAppDispatch();
  const { current: currentTenant } = useAppSelector((state) => state.tenant);
  const tenantId = currentTenant?.id;

  const [tenant, setTenant] = useState<TenantDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // General form
  const [general, setGeneral] = useState({
    name: '', displayName: '', timezone: 'UTC', locale: 'en',
    defaultCurrency: 'USD', dateFormat: 'MM/DD/YYYY',
    fiscalYearStartMonth: 1, weekStart: 'monday',
  });
  // Branding
  // Settings sub-objects
  const [notifications, setNotifications] = useState(NOTIF_DEFAULTS);
  // Refer & Earn program switch (partner-network-service; default ON).
  const [referralEnabled, setReferralEnabled] = useState(true);
  useEffect(() => {
    getReferralProgram().then((p) => setReferralEnabled(!!p.enabled)).catch(() => undefined);
  }, []);
  const [security, setSecurity] = useState(SECURITY_DEFAULTS);
  const [access, setAccess] = useState(ACCESS_DEFAULTS);

  useEffect(() => {
    // Resolve the tenant from the session token (not the possibly-stale Redux id).
    getCurrentTenant()
      .then((tn) => {
        setTenant(tn);
        // Correct a stale Redux tenant id so the rest of the app + the save
        // handlers act on the session's actual tenant (fixes the /tenants/:id 403).
        if (tn.id && tn.id !== currentTenant?.id) {
          dispatch(setCurrentTenant({ ...(currentTenant || {}), id: tn.id, name: tn.name } as any));
        }
        setGeneral({
          name: tn.name || '',
          displayName: tn.displayName || '',
          timezone: tn.timezone || 'UTC',
          locale: tn.locale || 'en',
          defaultCurrency: tn.defaultCurrency || 'USD',
          dateFormat: tn.dateFormat || 'MM/DD/YYYY',
          fiscalYearStartMonth: Number((tn.settings as any)?.general?.fiscalYearStartMonth) || 1,
          weekStart: ((tn.settings as any)?.general?.weekStart as string) || 'monday',
        });
        const s: TenantSettings = tn.settings || {};
        setNotifications({ ...NOTIF_DEFAULTS, ...(s.notifications || {}) });
        setSecurity({ ...SECURITY_DEFAULTS, ...(s.security || {}) });
        setAccess({ ...ACCESS_DEFAULTS, ...(s.access || {}) });
      })
      .catch((e) => toast.error(getApiErrorMessage(e, t('org.load_error'))))
      .finally(() => setLoading(false));
    // Runs once; getCurrentTenant resolves the tenant from the token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mergedSettings = (override: Partial<TenantSettings>): TenantSettings => ({
    ...(tenant?.settings || {}),
    notifications, security, access,
    ...override,
  });

  const saveGeneral = async () => {
    if (!tenantId) return;
    setSavingGeneral(true);
    try {
      const { fiscalYearStartMonth, weekStart, ...columns } = general;
      const updated = await updateTenant(tenantId, {
        ...columns,
        settings: mergedSettings({ general: { ...((tenant?.settings as any)?.general || {}), fiscalYearStartMonth, weekStart } }),
      } as any);
      setTenant(updated);
      if (currentTenant && updated.name !== currentTenant.name) {
        dispatch(setCurrentTenant({ ...currentTenant, name: updated.name }));
      }
      setOrgFormatPrefs({ dateFormat: general.dateFormat, weekStart: general.weekStart as 'monday' | 'sunday' });
      toast.success(t('org.general.saved'));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('org.general.save_error')), { duration: 6000 });
    } finally {
      setSavingGeneral(false);
    }
  };

  const saveSettingsSection = async (section: 'notifications' | 'security' | 'access', next: any) => {
    if (!tenantId) return;
    // optimistic local state already set by caller
    try {
      const settings = mergedSettings({ [section]: next });
      const updated = await updateTenant(tenantId, { settings });
      setTenant(updated);
      toast.success(t('org.saved_short'));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('org.save_retry')), { duration: 6000 });
    }
  };

  const handleLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setUploadingLogo(true);
    try {
      const raster = await toRasterLogo(file);
      const url = await uploadTenantLogo(raster);
      await saveTenantLogo(url);
      // Mirror to payroll so payslip PDFs pick it up (best-effort).
      try {
        await syncPayrollBrandingLogo(url);
      } catch {
        toast('Logo saved — payslip branding will sync shortly', { icon: 'ℹ️' });
      }
      setTenant((t) => (t ? { ...t, logoUrl: url } : t));
      if (currentTenant) dispatch(setCurrentTenant({ ...currentTenant, logo: `${url}?t=${Date.now()}` }));
      toast.success(t('org.branding.logo_updated'));
    } catch (err: any) {
      const detail = getApiErrorMessage(err);
      toast.error(getApiErrorMessage(err, detail ? `${t('org.branding.logo_failed')}: ${detail}` : t('org.branding.logo_failed')));
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('org.loading')}
      </div>
    );
  }

  if (!tenant) {
    return <div className="py-12 text-center text-muted-foreground">{t('org.no_context')}</div>;
  }

  const Toggle = ({ checked, onChange, title, desc }: { checked: boolean; onChange: (v: boolean) => void; title: string; desc: string }) => (
    <div className="flex items-center justify-between">
      <div>
        <h4 className="font-medium">{title}</h4>
        <p className="text-sm text-muted-foreground">{desc}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-6">
        <h1 className="text-h1 text-charcoal">{t('org.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('org.subtitle', { name: tenant?.name || t('org.your_org', 'your organization') })}</p>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="general">{t('org.tabs.general')}</TabsTrigger>
          <TabsTrigger value="access">{t('org.tabs.access')}</TabsTrigger>
          <TabsTrigger value="notifications">{t('org.tabs.notifications')}</TabsTrigger>
          <TabsTrigger value="security">{t('org.tabs.security')}</TabsTrigger>
          <TabsTrigger value="branding">{t('org.tabs.branding')}</TabsTrigger>
          <TabsTrigger value="integrations">{t('org.tabs.integrations', 'Integrations')}</TabsTrigger>
        </TabsList>

        {/* ---- General ---- */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Settings className="w-5 h-5" /> {t('org.general.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org-name">{t('org.general.name')}</Label>
                  <Input id="org-name" value={general.name} onChange={(e) => setGeneral({ ...general, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-display">{t('org.general.display_name')}</Label>
                  <Input id="org-display" value={general.displayName} placeholder={general.name}
                    onChange={(e) => setGeneral({ ...general, displayName: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>{t('org.general.workspace')}</Label>
                  <Input value={tenant?.slug || ''} readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-tz">{t('org.general.timezone')}</Label>
                  <select id="org-tz" className="w-full h-10 px-3 rounded-md border border-border bg-card"
                    value={general.timezone} onChange={(e) => setGeneral({ ...general, timezone: e.target.value })}>
                    {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                  {/* Says what this actually governs. Dates and times in the app
                      render in each VIEWER's own timezone, deliberately — this
                      setting is for the cases with no viewer to ask, which today
                      means scheduled jobs. Without the note people reasonably
                      expect it to change what they see, and report a bug when it
                      does not. */}
                  <p className="text-xs text-muted-foreground">
                    {t('org.general.timezoneHelp', {
                      defaultValue:
                        'Used when there is no person to ask — scheduled jobs such as reminders and overnight snapshots run on this clock. Dates and times you see in the app always follow your own device timezone.',
                    })}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-cur">{t('org.general.currency')}</Label>
                  <select id="org-cur" className="w-full h-10 px-3 rounded-md border border-border bg-card"
                    value={general.defaultCurrency} onChange={(e) => setGeneral({ ...general, defaultCurrency: e.target.value })}>
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-date">{t('org.general.date_format')}</Label>
                  <select id="org-date" className="w-full h-10 px-3 rounded-md border border-border bg-card"
                    value={general.dateFormat} onChange={(e) => setGeneral({ ...general, dateFormat: e.target.value })}>
                    {DATE_FORMATS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-lang">{t('org.general.language', 'Default language')}</Label>
                  <select id="org-lang" className="w-full h-10 px-3 rounded-md border border-border bg-card"
                    value={general.locale} onChange={(e) => setGeneral({ ...general, locale: e.target.value })}>
                    {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                  <p className="text-xs text-muted-foreground">{t('org.general.language_hint', 'New members start in this language; anyone can switch their own.')}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-fy">{t('org.general.fiscal_year', 'Fiscal year starts')}</Label>
                  <select id="org-fy" className="w-full h-10 px-3 rounded-md border border-border bg-card"
                    value={general.fiscalYearStartMonth} onChange={(e) => setGeneral({ ...general, fiscalYearStartMonth: Number(e.target.value) })}>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                  <p className="text-xs text-muted-foreground">{t('org.general.fiscal_year_hint', 'Used by reports and year-to-date views.')}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-week">{t('org.general.week_start', 'Week starts on')}</Label>
                  <select id="org-week" className="w-full h-10 px-3 rounded-md border border-border bg-card"
                    value={general.weekStart} onChange={(e) => setGeneral({ ...general, weekStart: e.target.value })}>
                    <option value="monday">{t('org.general.monday', 'Monday')}</option>
                    <option value="sunday">{t('org.general.sunday', 'Sunday')}</option>
                  </select>
                </div>
              </div>
              <Button onClick={saveGeneral} disabled={savingGeneral}>
                {savingGeneral ? t('org.general.saving') : t('org.general.save')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- Users & Access ---- */}
        <TabsContent value="access" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> {t('org.access.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Toggle title={t('org.access.auto_approve_t')} desc={t('org.access.auto_approve_d')}
                checked={access.autoApprove}
                onChange={(v) => { const n = { ...access, autoApprove: v }; setAccess(n); saveSettingsSection('access', n); }} />
              <Toggle title={t('org.access.require_mgr_t')} desc={t('org.access.require_mgr_d')}
                checked={access.requireManagerApproval}
                onChange={(v) => { const n = { ...access, requireManagerApproval: v }; setAccess(n); saveSettingsSection('access', n); }} />
              <Toggle title={t('org.access.self_service_t')} desc={t('org.access.self_service_d')}
                checked={access.selfServiceProfile}
                onChange={(v) => { const n = { ...access, selfServiceProfile: v }; setAccess(n); saveSettingsSection('access', n); }} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- Notifications ---- */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5" /> {t('org.notif.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Toggle title={t('org.notif.email_t')} desc={t('org.notif.email_d')}
                checked={notifications.emailNotifications}
                onChange={(v) => { const n = { ...notifications, emailNotifications: v }; setNotifications(n); saveSettingsSection('notifications', n); }} />
              <Toggle title={t('org.notif.leave_t')} desc={t('org.notif.leave_d')}
                checked={notifications.leaveRequests}
                onChange={(v) => { const n = { ...notifications, leaveRequests: v }; setNotifications(n); saveSettingsSection('notifications', n); }} />
              <Toggle title={t('org.notif.digest_t')} desc={t('org.notif.digest_d')}
                checked={notifications.weeklyDigest}
                onChange={(v) => { const n = { ...notifications, weeklyDigest: v }; setNotifications(n); saveSettingsSection('notifications', n); }} />
              {notifications.weeklyDigest && (
                <p className="text-xs text-muted-foreground pl-0 sm:pl-6">{t('org.notif.digest_hint', 'Sent every Monday morning to owners and admins — a summary of the last week\u2019s organization activity.')}</p>
              )}
            </CardContent>
          </Card>

          {/* Tenant notification policy (notification-service) — per category ×
              channel: Required / Optional / Off for everyone in the company. */}
          <NotificationPolicyCard />
          {/* The trail sits under the policy deliberately: the policy decides
              what SHOULD be sent, and this says what actually was. It hides
              itself for anyone who is not a tenant admin. */}
          <EmailDeliveryTrailCard />

          {/* Per-user channel preferences (notification-service) — the admin's
              own rows; the org-level switches above stay the tenant defaults. */}
          <NotificationPreferencesCard />

          {/* Sits with the notification preferences because it is the same
              kind of choice: what other people get to see about me. */}
          <ReadReceiptsCard />

          {/* Refer & Earn program (Partner Network) — org-level switch. Turning
              it off hides the entry points; history and earned rewards remain. */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Gift className="w-5 h-5" /> {t('org.refer.title', 'Refer & Earn')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Toggle
                title={t('org.refer.toggle_t', 'Allow employee referrals')}
                desc={t('org.refer.toggle_d', 'Employees can introduce other businesses to KaziPeople and earn a reward when they become paying customers.')}
                checked={referralEnabled}
                onChange={(v) => {
                  setReferralEnabled(v);
                  setReferralProgramEnabled(v)
                    .then(() => toast.success(v ? t('org.refer.on', 'Refer & Earn enabled') : t('org.refer.off', 'Refer & Earn disabled')))
                    .catch((e) => { setReferralEnabled(!v); toast.error(getApiErrorMessage(e, 'Could not update the setting')); });
                }} />
              {!referralEnabled && (
                <p className="text-xs text-muted-foreground pl-0 sm:pl-6">{t('org.refer.off_hint', 'The Refer & Earn entry points are hidden. Existing referrals and earned rewards are kept.')}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- Security ---- */}
        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> {t('org.security.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Toggle title={t('org.security.twofa_t')} desc={t('org.security.twofa_d')}
                checked={security.twoFactorRequired}
                onChange={(v) => { const n = { ...security, twoFactorRequired: v }; setSecurity(n); saveSettingsSection('security', n); }} />
              <Toggle title={t('org.security.session_t')} desc={t('org.security.session_d')}
                checked={security.sessionTimeout}
                onChange={(v) => { const n = { ...security, sessionTimeout: v }; setSecurity(n); saveSettingsSection('security', n);
                  setOrgSessionPolicy({ sessionTimeoutEnforced: v, sessionTimeoutMinutes: n.sessionTimeoutMinutes || 30 }); }} />
              {security.sessionTimeout && (
                <div className="flex items-center justify-between pl-0 sm:pl-6">
                  <p className="text-sm text-muted-foreground">{t('org.security.session_minutes_d', 'Sign members out after this much inactivity. A member may choose a shorter personal timeout, never a longer one.')}</p>
                  <select className="h-9 px-2 rounded-md border border-border bg-card text-sm"
                    value={security.sessionTimeoutMinutes || 30}
                    onChange={(e) => { const n = { ...security, sessionTimeoutMinutes: Number(e.target.value) }; setSecurity(n); saveSettingsSection('security', n);
                      setOrgSessionPolicy({ sessionTimeoutEnforced: n.sessionTimeout, sessionTimeoutMinutes: n.sessionTimeoutMinutes }); }}>
                    {SESSION_TIMEOUT_CHOICES.map((m) => <option key={m} value={m}>{m >= 60 ? `${m / 60}h` : `${m}m`}</option>)}
                  </select>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- Branding ---- */}
        <TabsContent value="branding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Palette className="w-5 h-5" /> {t('org.branding.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg overflow-hidden flex items-center justify-center bg-orange-50 dark:bg-orange-500/[0.08] border border-border">
                  {tenant?.logoUrl ? (
                    <img src={tenant.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Building2 className="w-8 h-8 text-orange-500" />
                  )}
                </div>
                <div>
                  <Label className="block mb-1">{t('org.branding.logo')}</Label>
                  <label className="inline-flex items-center px-3 py-2 rounded-md border border-border text-sm cursor-pointer hover:bg-muted">
                    {uploadingLogo ? t('org.branding.uploading') : t('org.branding.upload')}
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogo} />
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">{t('org.branding.logo_hint')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- Integrations — render the Email Integrations manager inline so it
             works regardless of nested-route matching (a separate route under
             /t/:slug/app was bouncing to the dashboard). ---- */}
        <TabsContent value="integrations" className="space-y-6">
          <BenefitsSettingsCard />
          <FinancialProductsSettingsCard />
          <CommerceSettingsCard />
          <CalendarSyncSettingsCard />
          <EmailIntegrationsPage />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
